import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { CatalogBrandTile } from './types'

/**
 * The narrow seam `useCatalogBrands` needs — `GET /catalog/brands` (UID-416
 * / ADR-0131), mirroring `useCategoryTaxonomy.ts`'s `CategoryListingService`
 * pattern: an injectable interface so tests never hit the network.
 */
export interface BrandListingService {
  listBrands(): Promise<readonly CatalogBrandTile[]>
}

/** Pull the `title` off a Problem+JSON error body for a user-facing message
 * (same mapping `useCatalogSearch.ts`'s `problemTitle` uses) — used only for
 * logging here, since D8 renders no visible error state for this panel. */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

async function bffListBrands(): Promise<readonly CatalogBrandTile[]> {
  const { data, error } = await apiClient.GET('/catalog/brands', {})
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load the brand panel.'))
  }
  return data.brands
}

export const defaultBrandListingService: BrandListingService = {
  listBrands: bffListBrands,
}

export type CatalogBrandsState =
  | { readonly status: 'loading' }
  /** UID-416 / ADR-0131 D8: a 503, any other error, or an empty `brands: []`
   * all collapse into this ONE state — the panel renders nothing, with no
   * error card and no skeleton left behind. Deliberately asymmetric with
   * `useCategoryTaxonomy`'s `error` state (which DOES render an `EmptyState`
   * + "Try again"): `ShopByCategory` is `/catalog`'s primary job, so its
   * failure is worth reporting; this supplementary panel's failure is not
   * worth degrading a page that still works. */
  | { readonly status: 'hidden' }
  | { readonly status: 'ready'; readonly brands: readonly CatalogBrandTile[] }

/**
 * Data-layer seam for the "Shop by brand" panel (UID-416 / ADR-0131 D10):
 * fetches the brand tiles once on mount — mirroring `useCategoryTaxonomy.ts`
 * almost line for line. Fetch on mount only; there is no query state to
 * react to (query-independent, like the category list).
 *
 * Deliberately NO `retry()` (D8): with no visible error state to retry
 * from, a retry action would have nothing to attach to.
 */
export function useCatalogBrands(
  service: BrandListingService = defaultBrandListingService,
): CatalogBrandsState {
  const [state, setState] = useState<CatalogBrandsState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })
    serviceRef.current
      .listBrands()
      .then((brands) => {
        if (cancelled) return
        // An empty `brands: []` collapses into the same `hidden` state as a
        // failure (D8) — the panel is driven entirely by the response, and
        // there is nothing honest to render for zero brands.
        setState(brands.length > 0 ? { status: 'ready', brands } : { status: 'hidden' })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setState({ status: 'hidden' })
        // Not surfaced to the user (D8's no-visible-error-state rule) — kept
        // as a dev-console breadcrumb only, mirroring the discipline other
        // hooks apply to a non-fatal background fetch.
        console.warn(problemTitle(error, 'We couldn’t load the brand panel.'))
      })
    return () => {
      cancelled = true
    }
    // `service` is read only via the ref; fetch on mount only (query-independent).
  }, [])

  return state
}
